import React, { useEffect, useRef, useCallback } from 'react';
import { Page } from 'react-pdf';

import { usePDFFormFields } from '../hooks/usePDFFormFields';
import TextFieldOverlay from './TextFieldOverlay';
import CheckboxFieldOverlay from './CheckboxFieldOverlay';
import RadioFieldOverlay from './RadioFieldOverlay';
import DropdownFieldOverlay from './DropdownFieldOverlay';
import TextAreaFieldOverlay from './TextAreaFieldOverlay';
import ImageOverlay from './ImageOverlay';
import { logger } from '../utils/logger';

import type {
  ImageData,
  InteractionState,
  FormField,
} from '../types/pdf.types';

interface PDFPageWrapperProps {
  pdfDocument: any;
  pageNumber: number;
  scale: number;
  isVisible: boolean;
  isZooming?: boolean;
  readOnly?: boolean;
  formData: Map<string, string | boolean | string[]>;
  images: ImageData[];
  interactionState: InteractionState;
  fieldTypeMapping: Map<string, string>;
  fieldRadioGroupMapping: Map<string, string>;
  onFieldChange: (
    fieldId: string,
    value: string | boolean | string[],
    fieldTypeMapping: Map<string, string>,
    fieldRadioGroupMapping: Map<string, string>,
  ) => void;
  onImageDelete: (imageId: string) => void;
  onImagePositionChange: (imageId: string, x: number, y: number) => void;
  onImageSizeChange: (imageId: string, width: number, height: number) => void;
  onFieldsDiscovered?: (fields: FormField[]) => void;
}

const PDFPageWrapper: React.FC<PDFPageWrapperProps> = ({
  pdfDocument,
  pageNumber,
  scale,
  isVisible,
  isZooming = false,
  readOnly = false,
  formData,
  images,
  interactionState,
  fieldTypeMapping,
  fieldRadioGroupMapping,
  onFieldChange,
  onImageDelete,
  onImagePositionChange,
  onImageSizeChange,
  onFieldsDiscovered,
}) => {
  const formFields = usePDFFormFields(pdfDocument, pageNumber, scale);
  const reportedFieldsRef = useRef<Set<string>>(new Set());
  const seededFieldsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (formFields.length > 0 && onFieldsDiscovered) {
      const newFields = formFields.filter(
        (f) => !reportedFieldsRef.current.has(f.id),
      );
      if (newFields.length > 0) {
        if (logger.isDebugEnabled()) {
          logger.debug(
            '[PDFPageWrapper] Discovered fields:',
            newFields.map((f) => ({
              id: f.id,
              type: f.type,
              name: f.name,
              value: f.value,
            })),
          );
        }
        newFields.forEach((f) => reportedFieldsRef.current.add(f.id));
        onFieldsDiscovered(newFields);
      }
    }
  }, [formFields, onFieldsDiscovered]);

  const handleFieldChange = useCallback(
    (fieldId: string, value: string | boolean | string[]) => {
      onFieldChange(fieldId, value, fieldTypeMapping, fieldRadioGroupMapping);
    },
    [fieldRadioGroupMapping, fieldTypeMapping, onFieldChange],
  );

  const handleImageDelete = useCallback(
    (imageId: string) => {
      onImageDelete(imageId);
    },
    [onImageDelete],
  );

  const handleImagePositionChange = useCallback(
    (imageId: string, x: number, y: number) => {
      onImagePositionChange(imageId, x, y);
    },
    [onImagePositionChange],
  );

  const handleImageSizeChange = useCallback(
    (imageId: string, width: number, height: number) => {
      onImageSizeChange(imageId, width, height);
    },
    [onImageSizeChange],
  );

  useEffect(() => {
    if (formFields.length === 0) return;
    formFields.forEach((field) => {
      if (seededFieldsRef.current.has(field.id)) return;
      if (formData.has(field.id)) {
        seededFieldsRef.current.add(field.id);
        return;
      }

      const { value } = field;
      const hasValue =
        // eslint-disable-next-line no-nested-ternary
        typeof value === 'boolean'
          ? true // Always seed boolean values (both true and false are meaningful)
          : Array.isArray(value)
          ? value.length > 0
          : value !== '';

      if (hasValue) {
        logger.debug(
          `[PDFPageWrapper] Seeding field ${field.id} (${field.type}):`,
          value,
        );
        handleFieldChange(field.id, value);
      } else {
        logger.debug(
          `[PDFPageWrapper] Skipping seed for field ${field.id} (${field.type}) - no meaningful value`,
        );
      }

      seededFieldsRef.current.add(field.id);
    });
  }, [formFields, formData, handleFieldChange]);

  return (
    <div
      data-page-number={pageNumber}
      style={{
        position: 'relative',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      <Page
        pageNumber={pageNumber}
        scale={scale}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />

      {isVisible &&
        !readOnly &&
        formFields
          .filter((field) => !field.hidden)
          .map((field) => {
            const effectiveField = readOnly
              ? { ...field, readOnly: true }
              : field;
            const rawValue = formData.get(field.id);

            if (field.type === 'text') {
              return (
                <TextFieldOverlay
                  key={field.id}
                  field={effectiveField}
                  value={typeof rawValue === 'string' ? rawValue : ''}
                  onChange={(value) => handleFieldChange(field.id, value)}
                  scale={scale}
                  isZooming={isZooming}
                />
              );
            }
            if (field.type === 'textarea') {
              return (
                <TextAreaFieldOverlay
                  key={field.id}
                  field={effectiveField}
                  value={typeof rawValue === 'string' ? rawValue : ''}
                  onChange={(value) => handleFieldChange(field.id, value)}
                  scale={scale}
                  isZooming={isZooming}
                />
              );
            }
            if (field.type === 'checkbox') {
              const checkedValue =
                rawValue !== undefined
                  ? (rawValue as boolean)
                  : (field.value as boolean);
              logger.debug(`[PDFPageWrapper] Rendering checkbox ${field.id}:`, {
                rawValue,
                fieldValue: field.value,
                checkedValue,
              });
              return (
                <CheckboxFieldOverlay
                  key={field.id}
                  field={effectiveField}
                  checked={checkedValue}
                  onChange={(value) => handleFieldChange(field.id, value)}
                  scale={scale}
                  isZooming={isZooming}
                />
              );
            }
            if (field.type === 'radio') {
              const checkedValue =
                rawValue !== undefined
                  ? (rawValue as boolean)
                  : (field.value as boolean);
              logger.debug(`[PDFPageWrapper] Rendering radio ${field.id}:`, {
                rawValue,
                fieldValue: field.value,
                checkedValue,
              });
              return (
                <RadioFieldOverlay
                  key={field.id}
                  field={effectiveField}
                  checked={checkedValue}
                  onChange={(checked) => handleFieldChange(field.id, checked)}
                  scale={scale}
                  isZooming={isZooming}
                />
              );
            }
            if (field.type === 'dropdown') {
              return (
                <DropdownFieldOverlay
                  key={field.id}
                  field={effectiveField}
                  value={
                    Array.isArray(rawValue)
                      ? rawValue
                      : (rawValue as string) || ''
                  }
                  onChange={(value) => handleFieldChange(field.id, value)}
                  scale={scale}
                  isZooming={isZooming}
                />
              );
            }
            return null;
          })}

      {/* Image Overlays (only for visible pages) */}
      {isVisible &&
        !readOnly &&
        images
          .filter((img) => img.pageNumber === pageNumber)
          .map((img) => {
            const deleteHandler = () => handleImageDelete(img.id);
            const positionHandler = (x: number, y: number) =>
              handleImagePositionChange(img.id, x, y);
            const sizeHandler = (w: number, h: number) =>
              handleImageSizeChange(img.id, w, h);

            return (
              <ImageOverlay
                key={img.id}
                image={img}
                scale={scale}
                onDelete={deleteHandler}
                onPositionChange={positionHandler}
                onSizeChange={sizeHandler}
                interactionState={interactionState}
                isZooming={isZooming}
              />
            );
          })}
    </div>
  );
};

export default React.memo(PDFPageWrapper);
